import React, { useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * Confirmation dialog for when pickup and dropoff locations are the same
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the dialog is open
 * @param {Function} props.onClose - Function to close the dialog (Change button)
 * @param {Function} props.onConfirm - Function to proceed with submission (Proceed button)
 * @param {boolean} props.isDarkMode - Whether dark mode is enabled
 */
const LocationConfirmDialog = ({ isOpen, onClose, onConfirm, isDarkMode = false }) => {
    // Handle Escape key to close
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Escape') {
            onClose();
        }
    }, [onClose]);

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            window.addEventListener('keydown', handleKeyDown);
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, handleKeyDown]);

    if (!isOpen) return null;

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return createPortal(
        <div
            onClick={handleBackdropClick}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10000,
                padding: '20px',
            }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="location-confirm-title"
            aria-describedby="location-confirm-description"
        >
            <div
                style={{
                    backgroundColor: isDarkMode ? 'var(--bg-secondary)' : 'var(--bg-primary)',
                    borderRadius: '12px',
                    maxWidth: '480px',
                    width: '100%',
                    padding: '32px',
                    position: 'relative',
                    boxShadow: isDarkMode 
                        ? '0 20px 60px rgba(0, 0, 0, 0.5)' 
                        : '0 20px 60px rgba(0, 0, 0, 0.3)',
                    animation: 'fadeIn 0.2s ease-in-out',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Title */}
                <h3
                    id="location-confirm-title"
                    style={{
                        margin: '0 0 16px 0',
                        fontSize: '20px',
                        fontWeight: '600',
                        fontFamily: 'Gilroy, sans-serif',
                        color: isDarkMode ? 'var(--text-primary)' : 'var(--heading-color)',
                        lineHeight: '1.4',
                    }}
                >
                    Location Confirmation
                </h3>

                {/* Message */}
                <p
                    id="location-confirm-description"
                    style={{
                        margin: '0 0 32px 0',
                        fontSize: '16px',
                        fontFamily: 'Gilroy, sans-serif',
                        color: isDarkMode ? 'var(--text-secondary)' : 'var(--text-secondary)',
                        lineHeight: '1.5',
                    }}
                >
                    Your destination is same as your pick up point, Do you want to continue?
                </p>

                {/* Buttons */}
                <div
                    style={{
                        display: 'flex',
                        gap: '12px',
                        justifyContent: 'flex-end',
                    }}
                >
                    {/* Change Button */}
                    <button
                        type="button"
                        onClick={onClose}
                        style={{
                            padding: '12px 24px',
                            fontSize: '16px',
                            fontFamily: 'Gilroy, sans-serif',
                            fontWeight: '500',
                            borderRadius: '8px',
                            border: `1px solid ${isDarkMode ? 'var(--border-color)' : 'var(--border-color)'}`,
                            backgroundColor: 'transparent',
                            color: isDarkMode ? 'var(--text-primary)' : 'var(--text-secondary)',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            minWidth: '100px',
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.backgroundColor = isDarkMode ? 'var(--bg-primary)' : 'var(--bg-secondary)';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                        }}
                        aria-label="Change location"
                    >
                        Change
                    </button>

                    {/* Proceed Button */}
                    <button
                        type="button"
                        onClick={onConfirm}
                        style={{
                            padding: '12px 24px',
                            fontSize: '16px',
                            fontFamily: 'Gilroy, sans-serif',
                            fontWeight: '500',
                            borderRadius: '8px',
                            border: 'none',
                            backgroundColor: 'var(--primary-color)',
                            color: 'var(--text-white)',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            minWidth: '100px',
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.backgroundColor = 'var(--primary-hover)';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'var(--primary-color)';
                        }}
                        aria-label="Proceed with booking"
                    >
                        Proceed
                    </button>
                </div>

                <style jsx>{`
                    @keyframes fadeIn {
                        from {
                            opacity: 0;
                            transform: scale(0.95);
                        }
                        to {
                            opacity: 1;
                            transform: scale(1);
                        }
                    }
                `}</style>
            </div>
        </div>,
        document.body
    );
};

export default LocationConfirmDialog;

